import { CollapsibleContent } from '@radix-ui/react-collapsible';
import { useEffect, useMemo, useState } from 'react';

import {
  Collapsible,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';

import { DataSelectorSimplifiedItem } from './data-selector-simplified-item';
import { DataSelectorTreeItem } from './data-selector-tree-item';
import {
  MentionTreeNode,
  flattenAndFilterMentionTree,
} from './data-selector-utils';
import { TestStepSection } from './test-step-section';

type DataSelectorNodeProps = {
  node: MentionTreeNode;
  depth: number;
  searchTerm: string;
  simplifiedView: boolean;
};

const DataSelectorNode = ({
  node,
  depth,
  searchTerm,
  simplifiedView,
}: DataSelectorNodeProps) => {
  const [expanded, setExpanded] = useState(false);

  // Simplified view flattens a step's whole subtree into leaves. It's scoped to
  // depth 0 (the step root): nested nodes — whose accumulated key path keeps
  // matching the 'data'/'body' filter — must not re-apply the flattening.
  const canShowSimplifiedMentions =
    depth === 0 &&
    !!node.children?.some(
      (child) =>
        child.key.includes("['data']") || child.key.includes("['body']"),
    );

  const displayedMentions: MentionTreeNode[] = useMemo(() => {
    if (!node.children) return [];

    return simplifiedView && canShowSimplifiedMentions
      ? flattenAndFilterMentionTree(node.children)
      : node.children;
  }, [node.children, simplifiedView, canShowSimplifiedMentions]);

  useEffect(() => {
    setExpanded(!!searchTerm);
  }, [searchTerm]);

  if (node.data.isTestStepNode) {
    return (
      <TestStepSection stepName={node.data.propertyPath}></TestStepSection>
    );
  }

  return (
    <Collapsible className="w-full" open={expanded} onOpenChange={setExpanded}>
      <>
        <CollapsibleTrigger asChild={true} className="w-full relative">
          {node.data.breadcrumb ? (
            <DataSelectorSimplifiedItem node={node} />
          ) : (
            <DataSelectorTreeItem
              node={node}
              expanded={expanded}
              setExpanded={setExpanded}
              depth={depth}
            />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="w-full">
          {node.children && node.children.length > 0 && (
            <div className="flex flex-col ">
              {displayedMentions.map((child) => (
                <DataSelectorNode
                  depth={depth + 1}
                  node={child}
                  key={child.key}
                  searchTerm={searchTerm}
                  simplifiedView={simplifiedView}
                ></DataSelectorNode>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </>
    </Collapsible>
  );
};
DataSelectorNode.displayName = 'DataSelectorNode';
export { DataSelectorNode };
